// 5.50.0 the data behind the calendar notices — ONE hook for the Today card (Main + HealthSherpa) and the red Today panel
// (Calendar page), replacing the separate 5.32.0 reminder pop-up poll and the 5.34.0 panel poll (same endpoints, same
// timing, same rules — nothing changes on the server):
//   today     GET /api/calendar/today every 60 s + at once after any calendar write in any InsureDocs window
//             (calendarToday.notifyCalendarChanged); state open / minimized / dismissed per user per day on the server.
//   reminders GET /api/calendar/reminders every 30 s; Dismiss is stored per user per occurrence on the server
//             (POST /api/calendar/reminders/dismiss) and announced to every other open window at once
//             (calendarNotices.announceReminderDismissed), so a dismissed reminder never alerts a second time.
// Both polls are background: silent on errors, keep the last state, nothing without a login token.
import { useCallback, useEffect, useRef, useState } from 'react';
import { calendarApi } from './api';
import { CHANGED_EVENT, CHANGED_KEY } from './calendarToday';
import { REMINDER_DISMISSED_EVENT, announceReminderDismissed, dismissedKeyFromStorage, matchReminders } from './calendarNotices';

const TODAY_POLL_MS = 60000;
const REMINDER_POLL_MS = 30000;

export default function useCalendarNotices() {
  const [today, setToday] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [gone, setGone] = useState(() => new Set());
  const [busy, setBusy] = useState(null);           // object_id of a task being completed
  const [err, setErr] = useState('');
  const seq = useRef(0);

  const loadToday = useCallback(async () => {
    const my = ++seq.current;
    try {
      if (!localStorage.getItem('token')) return;
      const r = await calendarApi.today();
      if (my === seq.current && r && r.data && Array.isArray(r.data.items)) setToday(r.data);
    } catch { /* silent: keep the last state */ }
  }, []);

  const loadReminders = useCallback(async () => {
    try {
      if (!localStorage.getItem('token')) return;
      const r = await calendarApi.reminders();
      if (r && r.data && Array.isArray(r.data.reminders)) setReminders(r.data.reminders);
    } catch { /* ignore transient */ }
  }, []);

  useEffect(() => {
    loadToday(); loadReminders();
    const a = setInterval(loadToday, TODAY_POLL_MS);
    const b = setInterval(loadReminders, REMINDER_POLL_MS);
    const onChange = () => { loadToday(); loadReminders(); };
    const onDismissed = (e) => { if (e && e.detail) setGone((s) => new Set(s).add(e.detail)); };
    const onStorage = (e) => {
      if (e.key === CHANGED_KEY) onChange();
      const k = dismissedKeyFromStorage(e);
      if (k) { setGone((s) => new Set(s).add(k)); loadReminders(); }
    };
    window.addEventListener(CHANGED_EVENT, onChange);
    window.addEventListener(REMINDER_DISMISSED_EVENT, onDismissed);
    window.addEventListener('storage', onStorage);
    return () => {
      clearInterval(a); clearInterval(b);
      window.removeEventListener(CHANGED_EVENT, onChange);
      window.removeEventListener(REMINDER_DISMISSED_EVENT, onDismissed);
      window.removeEventListener('storage', onStorage);
    };
  }, [loadToday, loadReminders]);

  const setState = async (state) => {
    if (!today) return;
    setToday((d) => ({ ...d, state }));
    try { const r = await calendarApi.todayState(state, today.day); if (r && r.data) setToday(r.data); } catch { loadToday(); }
  };

  const complete = async (i) => {
    setBusy(i.object_id); setErr('');
    try {
      await calendarApi.update(i.object_id, i.etag, { completed: true });
      // the update call itself announces the change (api.js), which reloads from the server
      setToday((d) => ({ ...d, items: d.items.filter((x) => x.id !== i.id), count: d.count - 1 }));
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not complete the task');
      loadToday();
    } finally { setBusy(null); }
  };

  const dismissReminder = async (r) => {
    setGone((s) => new Set(s).add(r.key));
    try {
      await calendarApi.dismiss(r.key, r.title);
      announceReminderDismissed(r.key);
    } catch {
      setGone((s) => { const n = new Set(s); n.delete(r.key); return n; });
    }
  };

  const firing = reminders.filter((r) => !gone.has(r.key));
  const { alerts, rest } = matchReminders(today && today.enabled ? today.items : [], firing);
  return { today, alerts, rest, busy, err, setState, complete, dismissReminder, reload: loadToday };
}
